/**
 * 需求来源：/partnership/home 重构为 React SSR 页（合作页 partnership_home.html 还原）。
 * 注意事项：
 *  1. 解析 ossHost / fullName（含 CSR 兜底，链路与参考页 printonDemand/home 一致）。
 *  2. 卡片列表来自后端接口 elastic-api/cj/partner/getAllTypePlatformList（POST，headers.language=en，
 *     返回 [err, body]，数据在 body.data，为分组数组：{ partnerType, partnerTypeName, list:[{ partnerId,
 *     partnerName, partnerLogo, partnerUrl, description }] }），映射为扁平卡片模型 cards + 去重分类 categories。
 *  3. 接口失败兜底空数组，不阻断页面。
 *  4. __seo 复用原页 SEO 文案；fullName 取 siteCheck.data('full-name')，缺省 CJdropshipping。
 *
 * @param {Object} ctx - SSR 直传 Koa ctx 或 ssrDataLoader 统一上下文
 * @param {Function} request - 框架注入的统一请求函数 async (config) => [err, body]
 * @returns {Object} { ossHost, fullName, cards, categories, __seo }
 */
async function fetchData(ctx, request) {
  let ossHost = '';
  const eggCtx = (ctx && ctx.app) ? ctx : (ctx && ctx.ctx);
  if (eggCtx && eggCtx.app && eggCtx.app.config) {
    const viewVar = eggCtx.app.config.view && eggCtx.app.config.view.variable;
    ossHost = (viewVar && (viewVar.OSS_HOST || viewVar.CDN_HOST)) || '';
  }
  // CSR 兜底：window.OSS_HOST → 全局 CDN_HOST（与 OSS_HOST 等值）
  if (!ossHost && typeof window !== 'undefined' && window.OSS_HOST) {
    ossHost = window.OSS_HOST;
  }
  if (!ossHost && typeof CDN_HOST !== 'undefined') {
    ossHost = CDN_HOST;
  }

  // fullName 取站点 full-name（如 CJdropshipping），缺省回退 'CJdropshipping'
  let fullName = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      fullName = eggCtx.siteCheck.data('full-name') || '';
    }
  } catch (e) { fullName = ''; }
  if (!fullName && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { fullName = window.siteCheck.data('full-name') || ''; } catch (e) { /* ignore */ }
  }
  if (!fullName) { fullName = 'CJdropshipping'; }

  // 260715：siteName 取站点 name（如 CJ），缺省回退 'CJ'，用于固定文案 {{name}} 插值
  let siteName = '';
  try {
    if (eggCtx && eggCtx.siteCheck && typeof eggCtx.siteCheck.data === 'function') {
      siteName = eggCtx.siteCheck.data('name') || '';
    }
  } catch (e) { siteName = ''; }
  if (!siteName && typeof window !== 'undefined' && window.siteCheck && typeof window.siteCheck.data === 'function') {
    try { siteName = window.siteCheck.data('name') || ''; } catch (e) { /* ignore */ }
  }
  if (!siteName) { siteName = 'CJ'; }

  // ===== 平台卡片：接口拉取分组列表，映射为扁平卡片 + 去重分类（失败兜底空数组，不阻断页面） =====
  const cards = [];
  const categories = [];
  if (typeof request === 'function') {
    try {
      // 需求来源：DEV-118199 补充需求一期（任务单 DEV-120997）——该接口已从 cucheng-app-web
      // 迁移到 cj-elastic-search-rest-api，故前缀由「无前缀（cucheng-app-web）」改为 elastic-api。
      // 同工程的 getRecommendPlatformList 走的就是 elastic-api/cj/partner/...（见 app/service/partnership.js）。
      //
      // 【发版顺序是硬约束】cj-elastic-search-service-project 必须【先于】本工程全量上线。
      // 顺序颠倒 → 请求打到尚不存在的接口 → 下面 catch 兜底成空数组 → 目录页卡片全空，
      // 但页面不报错、不白屏，属【静默故障】，只能靠人肉发现。
      //
      // 【回滚方式】把 url 改回 'cj/partner/getAllTypePlatformList' 即可秒级回滚——
      // 源接口在 cucheng-app-web 侧全程保留未动、未加 @Deprecated，无数据不可逆。
      // 【本地联调提示】需要把本接口指向本地后端时，把 url 临时改为绝对地址：
      //   'http://localhost:8070/elastic-api/cj/partner/getAllTypePlatformList'
      // 原因：CjHeader 的 useVipPrefix() 会把 elastic-api 前缀【无条件改写】为 website-api
      // （普通访客）/ website-vip-api（VIP），SSR 与 CSR 两条链路都改写；而
      // common/config/index.js 在 NODE_ENV=development 时又把 apiNode 的【所有】前缀强制
      // 覆盖为 common/api-base.js 的 pre 环境地址，单设 websiteApi 环境变量会被该循环盖掉。
      // useVipPrefix 与 getDomainByUrlSsr 都有 `if (url.startsWith('http')) return url` 的
      // 提前返回，故绝对地址可同时绕过「前缀改写」与「pre 映射」，且不影响页面其它接口。
      // 2026-08-03 已用此方式完成本地联调：不带 token 直连返回 5 组 39 条，SSR 页 39/39 卡片渲染正常。
      const res = await request({
        url: 'elastic-api/cj/partner/getAllTypePlatformList',
        method: 'post',
        data: {},
        headers: { language: 'en' },
      });
      // 后端返回两种结构：{ success:true, data:[...] } 或 { statusCode:200, result:[...] }
      // （与 common/utils.js statusCode200 的判定一致），故 data / result 双兜底取分组数组
      const body = (res && res[1]) || {};
      const groups = body.data || body.result || [];
      groups.forEach(function (group) {
        const catName = (group && group.partnerTypeName) || '';
        if (catName && categories.indexOf(catName) < 0) categories.push(catName);
        const list = (group && group.list) || [];
        list.forEach(function (p) {
          cards.push({
            category: catName,
            name: p.partnerName || '',
            logo: p.partnerLogo || '',
            description: p.description || '',
            link: p.partnerUrl || '',
          });
        });
      });
    } catch (e) {
      // 兜底空数组
    }
  }

  return {
    ossHost: ossHost,
    fullName: fullName,
    siteName: siteName,
    cards: cards,
    categories: categories,
    // 需求来源：docs/合作页、CJ Entity、Ads开户落地页3个/AI提示词.md 第35次提交（需求78）—— 更新合作页 SEO 文案（原文见文档）；
    // 品牌全名走 fullName（缺省 CJdropshipping），文案中「Partner with CJ」的 CJ 走 siteName（缺省 CJ），保持多站点一致。
    __seo: {
      title: `${fullName} Partnership Program | Grow Your Ecommerce Business`,
      description: `Join the ${fullName} Partnership Program to connect with millions of ecommerce sellers. Partner with ${siteName} to grow your platform, app, service, or brand together.`,
      keywords: `${fullName} partnership, ecommerce partnership, dropshipping partner, Shopify partner, sourcing partner, affiliate partnership, ecommerce ecosystem`,
    },
  };
}

module.exports = fetchData;
